import React, { useState, useEffect } from "react";
import "../styles/Timetable.css";

const Timetable = ({ group }) => {
  const initialTimetable = {
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  };

  const [timetable, setTimetable] = useState(initialTimetable);
  const [currentDay, setCurrentDay] = useState("Monday");
  const [subjects, setSubjects] = useState(Array(6).fill(""));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [timetableExists, setTimetableExists] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const periods = [
    { period: "1st Period", time: "8:50 - 9:50" },
    { period: "2nd Period", time: "9:50 - 10:50" },
    { period: "3rd Period", time: "11:10 - 12:00" },
    { period: "4th Period", time: "12:00 - 12:50" },
    { period: "5th Period", time: "1:40 - 2:30" },
    { period: "6th Period", time: "2:30 - 3:20" }
  ];

  useEffect(() => {
    const fetchTimetable = async () => {
      try {
        const response = await fetch(`https://backend-main-4.onrender.com/api/fetch-timetable?group=${group}`);
        if (response.ok) {
          const data = await response.json();
          setTimetable(data);
          setTimetableExists(true);
        } else {
          setTimetableExists(false);
        }
      } catch (error) {
        console.error("Error fetching timetable:", error);
        setTimetableExists(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimetable();
  }, [group]);

  useEffect(() => {
    if (isEditing) {
      const daySubjects = timetable[currentDay].map(entry => entry.subject);
      setSubjects(daySubjects);
    }
  }, [isEditing, currentDay, timetable]);

  const handleSaveTimetable = async () => {
    const updatedTimetable = {
      ...timetable,
      [currentDay]: subjects.map((subject, index) => ({
        subject: subject.trim() || "Free Period",
        period: periods[index].period,
      })),
    };

    setTimetable(updatedTimetable);
    setIsSaving(true);
    setError("");

    try {
      const response = await fetch("https://backend-main-4.onrender.com/save-timetable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ timetable: updatedTimetable, group }),
      });

      if (response.ok) {
        alert("Timetable saved successfully!");
        setIsEditing(false);
        setTimetableExists(true);
      } else {
        throw new Error("Failed to save timetable.");
      }
    } catch (error) {
      setError("Unable to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTimetable = async () => {
    try {
      const response = await fetch("https://backend-main-4.onrender.com/delete-timetable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group }),
      });

      if (response.ok) {
        setTimetable(initialTimetable);
        setIsEditing(false);
        setTimetableExists(false);
        alert("Timetable deleted successfully!");
      } else {
        throw new Error("Failed to delete timetable.");
      }
    } catch (error) {
      setError("Unable to delete. Please try again.");
    }
  };

  const handleInputChange = (day, periodIndex, value) => {
    setTimetable(prevTimetable => ({
      ...prevTimetable,
      [day]: prevTimetable[day].map((period, index) =>
        index === periodIndex ? { ...period, subject: value } : period
      )
    }));
  };

  const isFormValid = subjects.some((subject) => subject.trim() !== "");

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="timetable-container">
      <h3>{isEditing ? "Edit" : timetableExists ? "Your" : "Create"} Weekly Timetable for {group}</h3>

      {timetableExists && !isEditing ? (
        <>
          <div className="timetable">
            <table>
              <thead>
                <tr>
                  <th>Period</th>
                  {Object.keys(timetable).map((day) => (
                    <th key={day}>{day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.map((period, rowIndex) => (
                  <tr key={rowIndex}>
                    <td>{period.period}</td>
                    {Object.keys(timetable).map((day) => (
                      <td key={day}>
                        {timetable[day][rowIndex]?.subject || "Free Period"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={() => setIsEditing(true)} className="edit-button">Alter Timetable</button>
          <button onClick={handleDeleteTimetable} className="delete-button">Delete Timetable</button>
        </>
      ) : (
        <>
          <div className="form-group">
            <label htmlFor="day">Day:</label>
            <select
              id="day"
              value={currentDay}
              onChange={(e) => setCurrentDay(e.target.value)}
            >
              {Object.keys(timetable).map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          {periods.map((period, index) => (
            <div className="form-group" key={index}>
              <label htmlFor={`subject-${index}`}>
                {period.period} ({period.time}):
              </label>
              <input
                type="text"
                id={`subject-${index}`}
                value={subjects[index]}
                onChange={(e) => {
                  const newSubjects = [...subjects];
                  newSubjects[index] = e.target.value;
                  setSubjects(newSubjects);
                  handleInputChange(currentDay, index, e.target.value);
                }}
              />
            </div>
          ))}

          <button
            onClick={handleSaveTimetable}
            className="save-button"
            disabled={!isFormValid || isSaving}
          >
            {isSaving ? "Saving..." : "Save Timetable"}
          </button>
          {error && <p className="error-message">{error}</p>}
        </>
      )}
    </div>
  );
};

export default Timetable;
